/**
 * Meta Ads API returns currency values as strings in MINOR units (fils for AED, cents for USD,
 * paise for PKR/INR, etc.). 100 minor = 1 major. This helper converts to a major-unit number
 * and formats. Caller must pass the ad account's currency — defaulting to a guess silently
 * mislabels values for accounts in a different currency.
 */
export function formatMetaCurrency(value: string | undefined | null, currency: string): string {
  if (value === undefined || value === null || value === '') return '—';
  const num = parseInt(value, 10);
  if (isNaN(num)) return '—';
  const major = num / 100;
  if (!currency) return major.toFixed(2);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(major);
  } catch {
    return `${major.toFixed(2)} ${currency}`;
  }
}

/**
 * Meta Ads INSIGHTS values for `spend`, `cpc`, `cpm` are returned as strings in MAJOR currency units
 * (e.g. "12.50" = 12.50 of the account currency — NOT 12.50 minor units). This is different from
 * amount_spent on the ad account level which is in MINOR units. Insights = major, account-level = minor.
 */
export function formatMetaInsightsValue(value: string | undefined | null, currency: string): string {
  if (value === undefined || value === null || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  if (!currency) return num.toFixed(2);
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency}`;
  }
}

export function formatMetaCount(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(num));
}

export function formatMetaPercent(value: string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return `${num.toFixed(2)}%`;
}

export function metaAccountStatusLabel(status?: number): { label: string; color: 'success' | 'warning' | 'danger' | 'muted' } {
  switch (status) {
    case 1: return { label: 'Active', color: 'success' };
    case 2: return { label: 'Disabled', color: 'danger' };
    case 3: return { label: 'Unsettled', color: 'warning' };
    case 7: return { label: 'Pending Risk Review', color: 'warning' };
    case 8: return { label: 'Pending Settlement', color: 'warning' };
    case 9: return { label: 'In Grace Period', color: 'warning' };
    case 100: return { label: 'Pending Closure', color: 'danger' };
    case 101: return { label: 'Closed', color: 'danger' };
    default: return { label: status === undefined ? 'Unknown' : `Status ${status}`, color: 'muted' };
  }
}
